// src/hooks/usePayouts.js
import { useMemo } from 'react';
import { PAYOUT_STRUCTURES } from '../constants/payoutStructures';
import { isOpenCatalogGame } from '../constants/communityGames';

// $ -> cents helpers
const cents = (n) => Math.max(0, Math.round(Number(n || 0)));
const dollars = (c) => (cents(c) / 100).toFixed(2);

// ============================================================================
// SHOWDOWN PAYOUT BANDS (scaled by entrant count)
// ============================================================================
// MIRROR of SHOWDOWN_BANDS_BY_ENTRANTS in functions/modules/payoutCalculation.js.
// Any change here MUST be reflected there (and vice versa). Frontend uses these
// to show a live "what 1st would win right now" projection that updates as
// people join the lobby; backend uses them at seeding time to compute the
// actual on-chain payout shape.
//
// Design (2026-05-30 with Dennis):
//   - Top 3 individual prizes whenever paidCount >= 3
//   - Position 4+ grouped in a single "In The Money" band, split evenly
//   - Podium share decays 100% (n=2 WTA) → 80% (n=15 full grid)
//   - Every row sums to 100% (no leakage)
// ============================================================================
const SHOWDOWN_BANDS_BY_ENTRANTS = {
  2: [
    { tier: 'Champion', minRank: 1, maxRank: 1, percent: 100 },
  ],
  3: [
    { tier: 'Champion',  minRank: 1, maxRank: 1, percent: 65 },
    { tier: '2nd Place', minRank: 2, maxRank: 2, percent: 35 },
  ],
  4: [
    { tier: 'Champion',  minRank: 1, maxRank: 1, percent: 55 },
    { tier: '2nd Place', minRank: 2, maxRank: 2, percent: 30 },
    { tier: '3rd Place', minRank: 3, maxRank: 3, percent: 15 },
  ],
  5: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 50 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 28 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 15 },
    { tier: 'In The Money', minRank: 4, maxRank: 4, percent: 7  },
  ],
  6: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 52 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 27 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 14 },
    { tier: 'In The Money', minRank: 4, maxRank: 4, percent: 7  },
  ],
  7: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 48 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 26 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 14 },
    { tier: 'In The Money', minRank: 4, maxRank: 5, percent: 12 },
  ],
  8: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 50 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 26 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 12 },
    { tier: 'In The Money', minRank: 4, maxRank: 5, percent: 12 },
  ],
  9: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 46 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 25 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 14 },
    { tier: 'In The Money', minRank: 4, maxRank: 6, percent: 15 },
  ],
  10: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 46 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 25 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 14 },
    { tier: 'In The Money', minRank: 4, maxRank: 6, percent: 15 },
  ],
  11: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 44 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 24 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 12 },
    { tier: 'In The Money', minRank: 4, maxRank: 7, percent: 20 },
  ],
  12: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 44 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 24 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 12 },
    { tier: 'In The Money', minRank: 4, maxRank: 7, percent: 20 },
  ],
  13: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 42 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 24 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 14 },
    { tier: 'In The Money', minRank: 4, maxRank: 7, percent: 20 },
  ],
  14: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 40 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 25 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 15 },
    { tier: 'In The Money', minRank: 4, maxRank: 7, percent: 20 },
  ],
  15: [
    { tier: 'Champion',     minRank: 1, maxRank: 1, percent: 40 },
    { tier: '2nd Place',    minRank: 2, maxRank: 2, percent: 25 },
    { tier: '3rd Place',    minRank: 3, maxRank: 3, percent: 15 },
    { tier: 'In The Money', minRank: 4, maxRank: 7, percent: 20 },
  ],
};

function getShowdownBandsForEntrants(entrants) {
  const n = Math.floor(Number(entrants) || 0);
  if (n < 2) return SHOWDOWN_BANDS_BY_ENTRANTS[2];
  if (n > 15) return SHOWDOWN_BANDS_BY_ENTRANTS[15];
  return SHOWDOWN_BANDS_BY_ENTRANTS[n] || SHOWDOWN_BANDS_BY_ENTRANTS[15];
}

// --- map event -> structure id (matches your constants file ids)
export function resolvePayoutStructureId(ev, { isTournament, isRacing } = {}) {
  const mode = String(ev?.mode || ev?.format || '').toLowerCase();

  if (mode === 'h2h') return 'h2h-winner-take-all';

  if (mode === 'wgn') {
    const target = Math.min(6, Math.max(3, Number(ev?.streakTarget || ev?.wgnTarget || 3)));
    return `wgn-${target}`;
  }

  if (mode === 'ladder' || mode === 'season-ladder') return 'season-ladder-100';

  // Showdown events - single race with top half paid
  // Structure is determined by game type only (grid size is in the structure)
  if (mode === 'showdown') {
    const gameKey = String(ev?.game || '').toLowerCase();
    
    // Map game keys to structure ID format
    const gameCodeMap = {
      'gt7': 'GT7',    // GT7 uses uppercase in structure IDs
      'fzh': 'fzh',    // Forza uses lowercase
      'mkw': 'mkw',    // Mario Kart uses lowercase
      'irc': 'irc',    // iRacing uses lowercase
      'f25': 'f25',    // F1 25 uses lowercase
    };
    const game = gameCodeMap[gameKey] || gameKey.toUpperCase();
    
    // Showdown structure IDs are predetermined per game
    // GT7: racing-GT7-top-7 (15 grid, top 7 paid)
    // Forza: racing-fzh-top-12 (24 grid, top 12 paid)
    // MKW: racing-mkw-top-6 (12 grid, top 6 paid)
    // iRacing: racing-irc-top-12 (24 grid, top 12 paid)
    // F1 25: racing-f25-top-10 (20 grid, top 10 paid)
    
    // Map game to their specific showdown structure
    const showdownMap = {
      'GT7': 'top-7',   // 15 grid, top 7 paid
      'fzh': 'top-12',  // 24 grid
      'mkw': 'top-6',   // 12 grid
      'irc': 'top-12',  // 24 grid
      'f25': 'top-10',  // 20 grid
    };
    
    const topKey = showdownMap[game];
    if (topKey) {
      return `racing-${game}-${topKey}`;
    }
    
    return null;
  }

  // Racing events - based on game and heat count
  if (isRacing || mode === 'racing') {
    const gameKey = String(ev?.game || '').toLowerCase();
    
    // Map game keys to structure ID format (payoutStructures uses mixed case)
    const gameCodeMap = {
      'gt7': 'GT7',    // GT7 uses uppercase in structure IDs
      'fzh': 'fzh',    // Forza uses lowercase
      'mkw': 'mkw',    // Mario Kart uses lowercase
      'irc': 'irc',    // iRacing uses lowercase
      'f25': 'f25',    // F1 25 uses lowercase
    };
    const game = gameCodeMap[gameKey] || gameKey.toUpperCase();
    // FIXED: Check heatsCount (with 's') first - matches eventCreationHelpers field name
    const heatCount = Number(ev?.heatsCount || ev?.heatCount || ev?.heats || 0);
    
    // Map to closest size tier (now includes 2)
    if (heatCount >= 16384) return `racing-${game}-16k`;
    if (heatCount >= 8192)  return `racing-${game}-8k`;
    if (heatCount >= 4096)  return `racing-${game}-4k`;
    if (heatCount >= 2048)  return `racing-${game}-2k`;
    if (heatCount >= 1024)  return `racing-${game}-1k`;
    if (heatCount >= 512)   return `racing-${game}-512`;
    if (heatCount >= 256)   return `racing-${game}-256`;
    if (heatCount >= 128)   return `racing-${game}-128`;
    if (heatCount >= 64)    return `racing-${game}-64`;
    if (heatCount >= 32)    return `racing-${game}-32`;
    if (heatCount >= 16)    return `racing-${game}-16`;
    if (heatCount >= 8)     return `racing-${game}-8`;
    if (heatCount >= 4)     return `racing-${game}-4`;
    if (heatCount >= 2)     return `racing-${game}-2`;  // Added 2-heat support
    
    return null;
  }

  // brackets
  if (isTournament) {
    const size = Number(ev?.bracketSize || ev?.maxTeams || 0);
    if (size >= 16384) return 'bracket-16k-team';
    if (size >= 8192)  return 'bracket-8k-team';
    if (size >= 4096)  return 'bracket-4k-team';
    if (size >= 2048)  return 'bracket-2k-team';
    if (size >= 1024)  return 'bracket-1k-team';
    if ([512,256,128,64,32,16,8,4].includes(size)) return `bracket-${size}-team`;
  }

  return null;
}

// Turn payout bands into "stage rows" using the live pool.
// Each band's percent is split evenly across its rank range.
//
// Rounding: floor everywhere on percentage math (never overspend the pool),
// then add the rounding residue to the Champion band so sum-of-payouts equals
// net pool exactly. MIRROR of buildStageBands in
// functions/modules/payoutCalculation.js — keep in sync.
function buildStageBands(netPoolCents, bands) {
  if (!Array.isArray(bands) || !bands.length) return [];
  const pct = (v) =>
    v == null ? NaN : (typeof v === 'string' ? parseFloat(v.replace('%', '')) : Number(v));

  const built = bands.map(b => {
    const from = Number.isFinite(b?.minRank) ? Number(b.minRank)
               : Number.isFinite(b?.place)   ? Number(b.place)   : null;
    const to   = Number.isFinite(b?.maxRank) ? Number(b.maxRank)
               : Number.isFinite(b?.to)      ? Number(b.to)      : from;
    const count = Math.max(1, (to ?? from) - (from ?? 1) + 1);

    // Use tier if available (for showdown), otherwise stage
    const label = b.tier || b.stage || 'Prize';

    // Fixed per-place amount → no rounding work
    if (Number.isFinite(b?.amountCents)) {
      const perPlace = cents(b.amountCents);
      const total = perPlace * count;
      return { stage: label, from, to, count, totalCents: total, total: `$${dollars(total)}` };
    }
    // Percent-of-pool — floor the band total and the per-place share, then
    // reconcile totalCents to (perPlace × count) so band internals stay
    // consistent. Residue gets added to Champion below.
    const p = pct(b?.percent);
    // Crit model: round UP to the nearest whole Crit (non-monetary scoreboard,
    // so slightly over-allocating is fine and gives users clean numbers).
    const bandTotal = Math.ceil(cents(netPoolCents) * (Number.isFinite(p) ? p : 0) / 100);
    const perPlace = Math.ceil(bandTotal / count);
    const totalAfterFloor = perPlace * count;
    return {
      stage: label, from, to, count,
      totalCents: totalAfterFloor,
      total: `$${dollars(totalAfterFloor)}`,
    };
  }).filter(r => r.from != null).sort((a,b) => a.from - b.from);

  // Allocate rounding residue to the Champion band (position 1 holder) so
  // displayed payouts sum to the full net pool. Matches backend behavior.
  const distributedCents = built.reduce((sum, b) => sum + (b.totalCents || 0), 0);
  const residueCents = cents(netPoolCents) - distributedCents;
  if (residueCents > 0 && built.length > 0) {
    const championBand = built.find(b => b.from === 1) || built[0];
    championBand.totalCents += residueCents;
    championBand.total = `$${dollars(championBand.totalCents)}`;
  }

  return built;
}

// CORRECTED: Process racing subBands with two-stage percentage calculation
// Stage 1: Band gets X% of total pool
// Stage 2: SubBand gets Y% of that band's pool
function buildRacingStageBands(netPoolCents, bands, gridSize = 15) {
  if (!Array.isArray(bands) || !bands.length) return [];
  
  const results = [];
  
  bands.forEach(band => {
    // STAGE 1: Calculate the total pool for this BAND (percent of total pool)
    const bandPercent = Number(band.percent || 0);
    const bandPoolCents = Math.ceil(cents(netPoolCents) * bandPercent / 100); // round up (Crit model)
    
    // Process subBands if they exist (racing structure)
    if (Array.isArray(band.subBands) && band.subBands.length > 0) {
      band.subBands.forEach(subBand => {
        const { tier, places, percentOfBand } = subBand;
        
        // Parse places array [start, end] or just [single]
        let heatPlaceFrom, heatPlaceTo;
        if (Array.isArray(places)) {
          heatPlaceFrom = places[0];
          heatPlaceTo = places.length > 1 ? places[1] : places[0];
        } else {
          heatPlaceFrom = heatPlaceTo = places;
        }
        
        // STAGE 2: Calculate the pool for this TIER (percent of band pool)
        const tierPercent = Number(percentOfBand || 0);
        const tierPoolCents = Math.ceil(bandPoolCents * tierPercent / 100); // round up (Crit model)
        
        // Calculate how many racers are in this tier
        let count;
        
        if (band.stage === 'Finals') {
          // Finals placements are straightforward
          count = heatPlaceTo - heatPlaceFrom + 1;
        } else {
          // For elimination rounds (Heat of 2, Heat of 4, etc.)
          // Extract number of heats from stage name (e.g., "Heat of 4" -> 4).
          // Strip thousands separators — 'Heat of 1,024' parsed as 1 and
          // inflated the per-racer preview ~1000x.
          const heatCountMatch = String(band.stage).replace(/,/g, '').match(/Heat of (\d+)/);
          const numHeats = heatCountMatch ? parseInt(heatCountMatch[1]) : 1;
          
          // Each heat has gridSize racers, and places refer to positions within heat
          // Number of racers in this tier = number of heats Ã— racers per heat in this place range
          const racersPerHeat = heatPlaceTo - heatPlaceFrom + 1;
          count = numHeats * racersPerHeat;
        }
        
        // Format display label for better UX
        let displayLabel;
        if (band.stage === 'Finals') {
          // For Finals, show tier with place indication
          displayLabel = `${tier}`;
        } else {
          // For elimination rounds, show full context
          displayLabel = `${band.stage} - ${tier}`;
        }
        
        results.push({
          stage: displayLabel,
          from: heatPlaceFrom,  
          to: heatPlaceTo,
          count,
          totalCents: tierPoolCents,
          total: `$${dollars(tierPoolCents)}`,
        });
      });
    } else {
      // Fallback for non-racing bands
      const from = band.minRank || 1;
      const to = band.maxRank || from;
      const count = to - from + 1;
      
      results.push({
        stage: band.stage || 'Prize',
        from,
        to,
        count,
        totalCents: bandPoolCents,
        total: `$${dollars(bandPoolCents)}`
      });
    }
  });
  
  // Keep order as defined in structure (Finals first, then elimination rounds)
  return results;
}

/**
 * usePayouts
 * @param {object} opts.ev - event doc
 * @param {number} opts.entrants - actual # paying units (players or teams)
 * @param {number} opts.entryFeeCents - fee in cents
 * @param {boolean} opts.isTournament
 * @param {boolean} opts.isRacing - NEW: flag for racing events
 */
export default function usePayouts({
    ev,
    entrants = 0,
    entryFeeCents = 0,
    isTournament = false,
    isRacing = false,
    platformFeeRate = 0.10, // 10% rake
}) {
  return useMemo(() => {
    if (!ev) return { poolCents: 0, structureId: null, structureName: null, structureDesc: null, stageBands: [] };

    const psId = resolvePayoutStructureId(ev, { isTournament, isRacing });
    const struct = psId ? PAYOUT_STRUCTURES[psId] : null;

    // Live pool = actual entrants Ã— fee
    const grossPoolCents = cents(entrants) * cents(entryFeeCents);
    const rakeCents = Math.floor(grossPoolCents * platformFeeRate);
    const netPoolCents = Math.max(0, grossPoolCents - rakeCents);

    // Bands selection:
    //  - Showdowns scale by actual entrant count via SHOWDOWN_BANDS_BY_ENTRANTS
    //    so the displayed "what you could win right now" updates as racers
    //    join the lobby. This mirrors the backend at seeding time.
    //  - All other modes (h2h, bracket, racing) use the static structure
    //    or an event-level override.
    const mode = String(ev?.mode || ev?.format || '').toLowerCase();
    const isShowdown = mode === 'showdown';
    // Open-catalog grids (MKW / F1 25) settle on their FIXED top-N structure
    // (racing-mkw-top-6, racing-f25-top-10) — the entrant-scaled GT7-style
    // bands would preview a different shape than the one that pays.
    const usesFixedShowdownStructure = isShowdown
      && isOpenCatalogGame(String(ev?.game || '').toLowerCase())
      && struct?.bands?.length > 0;

    const bands = (isShowdown && !usesFixedShowdownStructure)
      ? getShowdownBandsForEntrants(entrants)
      : (struct?.bands?.length
          ? struct.bands
          : (Array.isArray(ev?.payoutBands) ? ev.payoutBands : []));

    const stageBands = (isRacing && !isShowdown)
      ? buildRacingStageBands(netPoolCents, bands, struct?.gridSize || 15)
      : buildStageBands(netPoolCents, bands);

    return {
      grossPoolCents,
      netPoolCents,
      structureId: psId,
      structureName: struct?.name || ev?.payoutName || null,
      structureDesc: struct?.description || ev?.payoutDescription || null,
      stageBands,
    };
  }, [ev, entrants, entryFeeCents, isTournament, isRacing, platformFeeRate]);
}